import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Typography } from '@mui/material';
import TestimonialCard from './TestmonialCards';
import './TestmonialCarousel.css';

import screenshotNvida from '../asset/testimonial_screenshots/nvida.jpg';
import screenshotMsft from '../asset/testimonial_screenshots/msft.jpg';
import screenshotMeta from '../asset/testimonial_screenshots/meta.jpg';
import screenshotJpm from '../asset/testimonial_screenshots/jpm.jpg';
import screenshotBoa from '../asset/testimonial_screenshots/boa.jpg';
import screenshotAdobeIndia from '../asset/testimonial_screenshots/adobe_india.jpg';
import screenshotEmailAce from '../asset/testimonial_screenshots/email_ace.jpg';
import screenshotEmailUbr from '../asset/testimonial_screenshots/email_ubr.jpg';

const TestimonialCarousel = ({ variant = 'default' }) => {
  const [items, setItems] = useState([]);
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const initialTestimonials = useMemo(
    () => [
      {
        name: "IIT Delhi CS '24",
        avatarLetter: 'I',
        brief: 'Just landed an Adobe offer.',
        screenshot: screenshotAdobeIndia,
      },
      {
        name: "Berkeley CS '25",
        avatarLetter: 'B',
        brief: 'Finally got the Microsoft offer.',
        screenshot: screenshotMsft,
      },
      {
        name: 'Meta E5',
        avatarLetter: 'M',
        brief: 'Cracked it and got a Meta offer.',
        screenshot: screenshotMeta,
      },
      {
        name: "NYU CS '24",
        avatarLetter: 'N',
        brief: 'Just signed my JPMorgan offer.',
        screenshot: screenshotJpm,
      },
      {
        name: "UT Austin CS '25",
        avatarLetter: 'U',
        brief: 'Landed a Bank of America offer—so relieved.',
        screenshot: screenshotBoa,
      },
      {
        name: "CMU ECE '24",
        avatarLetter: 'C',
        brief: 'Got the NVIDIA offer—dream come true.',
        screenshot: screenshotNvida,
      },
      {
        name: 'Google L4',
        avatarLetter: 'M',
        brief: variant === 'assistant' ? 'Aced the session—offer secured.' : 'Aced the interview—offer secured.',
        screenshot: screenshotEmailAce,
      },
      {
        name: "Waterloo SE '25",
        avatarLetter: 'W',
        brief: variant === 'assistant' ? 'Cracked my Uber technical after 1,000 applications.' : 'Cracked my Uber interview after 1,000 applications.',
        screenshot: screenshotEmailUbr,
      },
    ],
    [variant],
  );

  useEffect(() => {
    setItems(initialTestimonials);
  }, [initialTestimonials]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia('(max-width: 768px)');
    const update = (e) => setIsMobile(e.matches);

    setIsMobile(mq.matches);

    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollAmount = 0;
    const step = 1;

    const scroll = () => {
      scrollAmount += step;
      container.scrollTop = scrollAmount;

      if (scrollAmount >= container.scrollHeight / 2) {
        scrollAmount = 0;
        container.scrollTop = 0;
      }
    };

    const intervalId = setInterval(scroll, 50);

    return () => clearInterval(intervalId);
  }, [items]);

  const { leftColumnItems, rightColumnItems } = useMemo(() => {
    if (isMobile) return { leftColumnItems: items, rightColumnItems: [] };

    const left = [];
    const right = [];
    items.forEach((item, idx) => {
      if (idx % 2 === 0) left.push(item);
      else right.push(item);
    });
    return { leftColumnItems: left, rightColumnItems: right };
  }, [items, isMobile]);

  return (
    <div className="testimonial-carousel">
      <Typography variant="h4" align="center" gutterBottom>
        Success Stories 🚀
      </Typography>
      <div className="social-proof-row">
        <div className="social-proof-item">
          <span className="social-proof-number">21,000+</span>
          <span className="social-proof-label">
            {variant === 'assistant' ? 'people use Convocue' : 'job seekers use Convocue'}
          </span>
        </div>
        <div className="social-proof-divider" />
        <div className="social-proof-item">
          <span className="social-proof-stars" aria-hidden="true">★★★★★</span>
          <span className="social-proof-number">4.9</span>
          <span className="social-proof-label">from 1,800+ reviews</span>
        </div>
      </div>

      <div className="fade-overlay"></div>
      <div ref={containerRef} className="carousel-container">
        <div className="carousel-columns">
          <div className="carousel-column">
            {[...leftColumnItems, ...leftColumnItems].map((testimonial, index) => (
              <div key={`left-${index}`} className="carousel-item">
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}
          </div>
          <div className="carousel-column">
            {[...rightColumnItems, ...rightColumnItems].map((testimonial, index) => (
              <div key={`right-${index}`} className="carousel-item">
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}
          </div>
        </div>

      </div>
                      <div className="fade-overlay-bottom"></div>

    </div>
  );
};

export default TestimonialCarousel;
